import React from 'react';

import { t } from 'i18next';
import Skeleton from '../../Skeleton';
import * as QuestCard from './styles';

import { faArrowAltCircleRight, faCircleCheck, faCircleXmark, faClock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon as FAIcon } from "@fortawesome/react-fontawesome";

import nf from '@tuplo/numberfmt';
import Markdown from 'markdown-to-jsx';

interface CompanyQuestsPreviewCardProps extends LoadingProps {
  image: string;
  name: string;
  description: string;
  amount_formatted: string;
  pending_quests_count?: number;
  onClick?: () => void;
}

interface SingleQuestCardProps {
  title: string;
  description: string;
  reward_amount_formatted: string;
  period_type?: string;
  is_budget?: boolean;
  quest_completed?: boolean;
  time_seconds?: number;
  notClickable?: boolean;
  onClick?: () => void;
}

const LargeCardLoading: React.FC = () => {
  return <QuestCard.LargeContainer>
    <Skeleton style={{ height: '100%', width: '100%', borderRadius: 22 }}>
    </Skeleton>
  </QuestCard.LargeContainer>
}

const CompanyQuestsPreviewCard: React.FC<CompanyQuestsPreviewCardProps> = ({
  image,
  name,
  description,
  amount_formatted,
  onClick,
}) => {

  return (
    <QuestCard.LargeContainer title={name} onClick={onClick}>
      <div className='logo-wrapper'>
        <QuestCard.Logo
          src={image}
          onError={(event: any) => {
            event.target.onerror = null;
          }}
          style={{ height: 48 }}
        />
      </div>
      <QuestCard.Description>
        {description}
      </QuestCard.Description>
      <QuestCard.EarningInfo>
        {/* <QuestCard.EarningInfoIcon src='/images/coins/btc-large-icon.png'/> */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: '100%' }}>
            <QuestCard.Amount>{amount_formatted}</QuestCard.Amount>
          </div>
        </div>
      </QuestCard.EarningInfo>
    </QuestCard.LargeContainer>
  );
};

const VerticalCompanyQuestsPreviewCard: React.FC<CompanyQuestsPreviewCardProps> = ({
  image,
  name,
  description,
  pending_quests_count,
  onClick,
}) => {

  return (
    <QuestCard.VerticalContainer title={name} onClick={onClick}>
      {pending_quests_count !== null && pending_quests_count !== 0 ? (
        <QuestCard.PendingQuests>{pending_quests_count}</QuestCard.PendingQuests>
      ) : (null)}
      <QuestCard.Logo
        src={image}
        onError={(event: any) => {
          event.target.onerror = null;
        }}
      />
      <div style={{ display: 'flex', paddingLeft: 10, gap: 10, flex: '1', height: '100%'}}>
        <div style={{ display: 'flex', flexDirection: 'column', flex: '1', height: '100%', alignContent: 'center', justifyContent: 'center' }}>
          <QuestCard.ActionText style={{ marginTop: '0px' }}>{name}</QuestCard.ActionText>
          <QuestCard.Description style={{ overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
            <Markdown>{description}</Markdown>
          </QuestCard.Description>
        </div>
        <h1 style={{ alignSelf: 'center', textAlign: 'center', marginRight: '10px' }}>
          {'>'}
        </h1>
      </div>
    </QuestCard.VerticalContainer>
  );
};

const VerticalCardLoading: React.FC = () => {

  return (
    <QuestCard.VerticalContainer>
      <Skeleton style={{ height: '100%', width: '100%', borderRadius: 10 }}>
      </Skeleton>
    </QuestCard.VerticalContainer>
  );
};

const CardLoading: React.FC = () => {
  return <QuestCard.Container>
    <Skeleton style={{ height: '100%', width: '100%', borderRadius: 22 }}>
    </Skeleton>
  </QuestCard.Container>
}

const SingleQuest: React.FC<SingleQuestCardProps> = ({
  title,
  description,
  time_seconds,
  reward_amount_formatted,
  period_type,
  is_budget,
  quest_completed,
  notClickable,
  onClick
}) => {

  function getPeriodFormatted(): string {
    switch (period_type) {
      case 'DAILY':
        return t('components.quests.daily');
      case 'WEEKLY':
        return t('components.quests.weekly');
      case 'MONTHLY':
        return t('components.quests.monthly');
      default:
        return '';
    }
  }

  function getTimeToComplete() {
    if (!time_seconds) {
      return '';
    }

    let formatted = '';

    const m = Math.floor(time_seconds / 60);

    if (m > 0) {
      formatted = m + 'm';
    }

    const s = time_seconds % 60;

    if (s > 0) {
      if (formatted.length > 0) {
        formatted = formatted + ' ';
      }

      formatted = formatted + s + 's';
    }

    return formatted;
  }

  const getRewardAmount = () => {
    const [value, unit] = reward_amount_formatted.split(' ');
    const shortValue = nf(value, '0a');

    return `${shortValue} ${unit}`;
  }

  return (
    <QuestCard.Container
      disabled={!is_budget}
      notClickable={notClickable}
      title={title}
      onClick={notClickable ? null : onClick}
    >
      {/* <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <span>{getPeriodFormatted()}</span>
        <span>{getTimeToComplete()}</span>
      </div> */}
      <QuestCard.ActionText>{title}</QuestCard.ActionText>
      <QuestCard.Description>{description}</QuestCard.Description>
      <hr style={{ border: 'none' }} />
      <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', width: '100%', justifyContent: 'flex-end' }}>
        <QuestCard.EarningInfo style={{ flex: '1', textAlign: 'left' }}>
          {/* <QuestCard.EarningInfoIcon src='/images/coins/btc-large-icon.png'/> */}
          <div>
            <QuestCard.Amount>{getRewardAmount()}</QuestCard.Amount>
          </div>
        </QuestCard.EarningInfo>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '3px', alignItems: 'center' }}>
          <span style={{ textAlign: 'end', alignContent: 'center', paddingTop: '2px' }} >{getTimeToComplete()}</span>
          <FAIcon icon={faClock} style={{ height: '16px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '2px', alignItems: 'center', marginLeft: '2px' }}>
          {is_budget ? (
            quest_completed ?
              (
                // <QuestCard.Description style={{ color: '#1d9820', fontWeight: 700 }}>{'Completado'}</QuestCard.Description>
                <FAIcon icon={faCircleCheck} style={{ height: '16px', color: '#1d9820' }} />
              )
              :
              (
                // <QuestCard.Description style={{ color: '#fe501c', fontWeight: 700 }}>{'Completar missão'}</QuestCard.Description>
                <FAIcon icon={faArrowAltCircleRight} style={{ height: '16px', color: '#fe501c' }} />
              )
          ) :
            (
              // <QuestCard.Description style={{ color: '#fe501c', fontWeight: 700 }}>{'Indisponível'}</QuestCard.Description>
              <FAIcon icon={faCircleXmark} style={{ height: '16px', color: '#fe501c' }} />
            )
          }
        </div>
      </div>
    </QuestCard.Container>
  );
};

export default {
  CompanyQuestsPreviewCard,
  LargeCardLoading,
  VerticalCompanyQuestsPreviewCard,
  VerticalCardLoading,
  SingleQuest,
  CardLoading,
  ...QuestCard,
};
